import { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { crm } from "../../../theme";
import { getProcessesByCvfRef } from "../../../services/processService";

interface ProcessMatch {
  id: string;
  cvfRef?: string;
  cvfSubRef?: string;
  clientName?: string;
}

/** Busca o processo pelo número CVF (ex.: "1451") e devolve o id escolhido. */
const LinkProcessModal = ({
  isOpen,
  onClose,
  quoteNumber,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  quoteNumber?: string;
  /** Devolve mensagens de erro (ou null se deu certo, e o modal fecha). */
  onConfirm: (processId: string) => Promise<string[] | null>;
}) => {
  const [term, setTerm] = useState("");
  const [matches, setMatches] = useState<ProcessMatch[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const close = () => {
    setTerm("");
    setMatches(null);
    setErrors([]);
    onClose();
  };

  const search = async () => {
    if (!term.trim()) return;
    setBusy(true);
    setErrors([]);
    const found = await getProcessesByCvfRef(term.trim());
    setMatches(Array.isArray(found) ? found : []);
    setBusy(false);
  };

  const choose = async (id: string) => {
    setBusy(true);
    const problems = await onConfirm(id);
    setBusy(false);
    if (problems) setErrors(problems);
    else close();
  };

  return (
    <Modal isOpen={isOpen} onClose={close} size="md">
      <ModalOverlay />
      <ModalContent borderRadius={crm.radius}>
        <ModalHeader fontSize="18px">Vincular processo{quoteNumber ? ` ao orçamento ${quoteNumber}` : ""}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex gap="8px">
            <Input
              size="sm"
              placeholder="Número do processo (ex.: 1451)"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
            />
            <Button size="sm" bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isLoading={busy} onClick={search}>
              Buscar
            </Button>
          </Flex>
          {matches && matches.length === 0 && (
            <Text mt="12px" fontSize="13px" color={crm.textMuted}>
              Nenhum processo encontrado.
            </Text>
          )}
          <Box mt="12px">
            {matches?.map((m) => (
              <Flex
                key={m.id}
                align="center"
                justify="space-between"
                p="8px 10px"
                border={`1px solid ${crm.border}`}
                borderRadius={crm.radiusSm}
                mb="6px"
              >
                <Box>
                  <Text fontSize="13px" fontWeight="600">
                    {[m.cvfSubRef, m.cvfRef].filter(Boolean).join(" ")}
                  </Text>
                  <Text fontSize="12px" color={crm.textMuted}>
                    {m.clientName ?? "—"}
                  </Text>
                </Box>
                <Button size="xs" variant="outline" isDisabled={busy} onClick={() => choose(m.id)}>
                  Vincular
                </Button>
              </Flex>
            ))}
          </Box>
          {errors.map((e) => (
            <Text key={e} mt="8px" fontSize="13px" color={crm.danger}>
              {e}
            </Text>
          ))}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={close}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LinkProcessModal;
